import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Tooltip from '../../components/Tooltip';
import './auth.css';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Membership section — visibility + info text driven by admin settings
  const [showMembership, setShowMembership] = useState(true);
  const [membershipText, setMembershipText] = useState(
    'The membership status of all prize winners will be confirmed before prizes are awarded'
  );

  useEffect(() => {
    api.get('/api/contestants/settings')
      .then((res) => {
        const s = res.data.settings;
        if (s.show_membership_section === false) setShowMembership(false);
        if (s.membership_info_text)              setMembershipText(s.membership_info_text);
      })
      .catch(() => { /* use defaults if settings unavailable */ });
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { is_member: 'non-member' } });

  // Watch the password field so confirm_password can validate against it.
  const password = watch('password');

  // Already logged in? Go straight to the contestant dashboard.
  useEffect(() => {
    if (!loading && user) {
      navigate('/contestant/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const onSubmit = async (data) => {
    setServerError('');
    try {
      // Create the user account; is_member is stored on users.is_member and
      // copied into contestants.is_member when they first submit a song.
      const res = await api.post('/api/auth/register', {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        full_name: data.full_name.trim(),
        role: 'contestant',
        is_member: data.is_member === 'member',
      });
      login(res.data.token, res.data.user);

      // Create the contestant profile now so the dashboard/profile pages
      // have a row to read from immediately, instead of waiting until the
      // user submits their first song (ContestantSubmit.js creates it lazily
      // as a fallback if this call fails, e.g. registration window closed
      // between account creation and this request).
      try {
        await api.post('/api/contestants/register', {
          phone: data.phone?.trim() || undefined,
        });
      } catch (profileErr) {
        console.error('Contestant profile creation failed:', profileErr);
      }

      navigate('/contestant/dashboard', { replace: true });
    } catch (err) {
      setServerError(
        err.response?.data?.error ?? 'Registration failed. Please try again.'
      );
    }
  };

  // NOTE: /register is fully public — do NOT gate on `loading` here.
  // The redirect useEffect above only fires when `user` is truthy (already
  // logged in), so unauthenticated users always see the form immediately with
  // no blank-page flash.

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">

        {/* ── FOOM header ── */}
        <div className="auth-card__header">
          <img src="/assets/SCALA_logo.png" alt="SCALA logo" className="auth-foom__logo" />
          <div>
            <p className="auth-foom__subtitle">The SCALA Festival<br />of Original Music</p>
            <p className="auth-foom__name">FOOM</p>
            <p className="auth-foom__tagline">CELEBRATING SONGWRITING</p>
          </div>
        </div>

        <div className="auth-card__gold-divider" />

        <div className="auth-card__body">

        <h2 className="auth-form-heading">Create Your Account</h2>

        {/* ── Server error banner ── */}
        {serverError && (
          <div className="auth-alert" role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>

          {/* Full Name */}
          <div className="form-group">
            <label className="form-label" htmlFor="full_name">
              Full name
              <Tooltip tooltipKey="contestant_full_name" />
            </label>
            <input
              id="full_name"
              type="text"
              autoComplete="name"
              autoFocus
              className={`form-input${errors.full_name ? ' is-error' : ''}`}
              placeholder="e.g. Jane Smith"
              {...register('full_name', {
                required: 'Full name is required.',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters.',
                },
                maxLength: {
                  value: 255,
                  message: 'Name must be 255 characters or fewer.',
                },
              })}
            />
            {errors.full_name && (
              <p className="field-error" role="alert">{errors.full_name.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email address
              <Tooltip tooltipKey="contestant_email" />
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`form-input${errors.email ? ' is-error' : ''}`}
              placeholder="you@example.com"
              {...register('email', {
                required: 'Email is required.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address.',
                },
              })}
            />
            {errors.email && (
              <p className="field-error" role="alert">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`form-input${errors.password ? ' is-error' : ''}`}
                {...register('password', {
                  required: 'Password is required.',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters.',
                  },
                })}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <p className="field-error" role="alert">{errors.password.message}</p>
            )}
          </div>

          {/* Confirm password */}
          <div className="form-group">
            <label className="form-label" htmlFor="confirm_password">
              Confirm password
            </label>
            <input
              id="confirm_password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`form-input${errors.confirm_password ? ' is-error' : ''}`}
              {...register('confirm_password', {
                required: 'Please confirm your password.',
                validate: (value) =>
                  value === password || 'Passwords do not match.',
              })}
            />
            {errors.confirm_password && (
              <p className="field-error" role="alert">
                {errors.confirm_password.message}
              </p>
            )}
          </div>

          {/* Phone (optional) */}
          <div className="form-group">
            <label className="form-label" htmlFor="phone">
              Phone number
              <span className="form-label-optional">(optional)</span>
              <Tooltip tooltipKey="contestant_phone" />
            </label>
            <input
              id="phone"
              type="tel"
              autoComplete="tel"
              className={`form-input${errors.phone ? ' is-error' : ''}`}
              placeholder="e.g. 04XX XXX XXX"
              {...register('phone', {
                pattern: {
                  value: /^[\d\s()+\-]{7,20}$/, // eslint-disable-line no-useless-escape
                  message: 'Enter a valid phone number (7–20 digits).',
                },
              })}
            />
            {errors.phone && (
              <p className="field-error" role="alert">{errors.phone.message}</p>
            )}
          </div>

          {/* SCALA Membership — hidden when admin has disabled the section */}
          {showMembership && (
            <div className="form-group">
              <span className="form-label" id="membership-label">
                SCALA membership
                <Tooltip tooltipKey="contestant_membership" />
              </span>
              {membershipText && (
                <p className="membership-info-text">{membershipText}</p>
              )}
              <div className="radio-group" role="radiogroup" aria-labelledby="membership-label">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="member"
                    {...register('is_member')}
                  />
                  Member
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="non-member"
                    {...register('is_member')}
                  />
                  Non-member
                </label>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.4rem' }}>
                To check your SCALA membership, <a href="https://scala.org.au/login/" target="_blank" rel="noopener noreferrer">click here</a>.
              </p>
            </div>
          )}

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Creating account…
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="auth-footer">
          Already have an account?{' '}
          <Link to="/login">Sign in</Link>
        </p>

        </div>{/* auth-card__body */}
      </div>
    </div>
  );
}
